import { doc, getDoc, serverTimestamp, setDoc } from 'firebase/firestore';
import type { User } from 'firebase/auth';
import { getFirebase } from '../firebase/config';
import { seedCatalog } from './album';
import { writeAudit } from './audit';
import { emailDocId } from './ids';

export async function loadAccessAdmins(): Promise<{ exists: boolean; admins: string[] }> {
  const { db } = getFirebase();
  const snap = await getDoc(doc(db, 'config', 'access'));
  if (!snap.exists()) return { exists: false, admins: [] };
  const admins = snap.data().admins;
  return {
    exists: true,
    admins: Array.isArray(admins) ? admins.map((e) => String(e).toLowerCase()) : [],
  };
}

/** First signed-in account writes the lock. Losers of the race get false. */
export async function tryClaimFirstAdmin(user: User, email: string): Promise<boolean> {
  const { db } = getFirebase();
  const accessRef = doc(db, 'config', 'access');
  try {
    await setDoc(accessRef, {
      admins: [email],
      createdAt: serverTimestamp(),
    });
  } catch {
    return false;
  }
  try {
    await setDoc(doc(db, 'allowlist', email), {
      email,
      role: 'admin',
      createdBy: user.uid,
      createdAt: serverTimestamp(),
    });
    await writeAudit(user.uid, email, 'first_admin');
  } catch (err) {
    console.error(err);
  }
  try {
    await seedCatalog();
    await writeAudit(user.uid, email, 'seed_catalog');
  } catch (err) {
    console.error(err);
  }
  return true;
}

export async function ensureAdminAllowlistRow(user: User, email: string): Promise<void> {
  const { db } = getFirebase();
  const ref = doc(db, 'allowlist', email);
  const snap = await getDoc(ref);
  if (snap.exists()) return;
  await setDoc(ref, {
    email,
    role: 'admin',
    createdBy: user.uid,
    createdAt: serverTimestamp(),
  });
}

export function googleEmail(user: { email: string | null }): string | null {
  if (!user.email) return null;
  try {
    return emailDocId(user.email);
  } catch {
    return null;
  }
}

export function googleDisplayName(user: User): string | null {
  const fromGoogle = user.providerData.find((p) => p.providerId === 'google.com')?.displayName;
  const name = (fromGoogle || user.displayName || '').trim();
  return name || null;
}
